import axios from 'axios';

interface OcrResponse {
    code: number;
    message: string;
    result: string;
    probability?: number;
}

interface CaptchaResponse {
    code: number;
    data: {
        captchaId: string;
    };
}

// 基本的OCR識別函數
async function recognizeBase64(base64Image: string, beta: boolean = false, probability: boolean = false): Promise<OcrResponse> {
    try {
        // 如果base64字串包含data URI scheme，需要移除它
        const base64Data = base64Image.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');

        const response = await axios.post<OcrResponse>(process.env.NEXT_PUBLIC_API_URL + '/ddocr/ocr/base64', {
            image: base64Data,
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        if (response.status !== 200) {
            throw new Error(`OCR請求失敗: HTTP狀態碼 ${response.status}`);
        }

        return response.data;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { status: number; data?: { message?: string } }; message: string };
            if (axiosError.response) {
                throw new Error(`OCR請求失敗: ${axiosError.response.status} - ${axiosError.response.data?.message || axiosError.message}`);
            }
        }
        throw new Error('OCR請求失敗，請稍後再試');
    }
}

// 將 Blob 轉換為 Base64 字串
async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                // 移除 Data URL 的前綴，只保留 Base64 部分
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            } else {
                reject(new Error('Failed to convert blob to base64'));
            }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// 使用 OCR API 辨識驗證碼
async function recognizeCaptcha(base64String: string): Promise<string> {
    try {
        const response = await recognizeBase64(base64String);
        return response.result;
    } catch (error) {
        console.error('驗證碼識別失敗:', error);
        throw new Error('驗證碼識別失敗');
    }
}

// 取得驗證碼圖片並辨識
async function getCaptcha() {
    try {
        // 1. 先取得 captchaId
        let generateResponse;
        try {
            const url = 'https://prod-mail.habbyservice.com/Capybara/api/v1/captcha/generate';
            generateResponse = await fetch(url, {
                method: 'POST'
            });
        } catch (error) {
            console.error('生成驗證碼請求失敗:', error);
            return { captchaId: '', captchaText: '' };
        }

        const generateResult = await generateResponse.json();
        const captchaId = generateResult.data.captchaId;

        // 2. 用 captchaId 取得圖片
        let imageResponse;
        try {
            const url = `https://prod-mail.habbyservice.com/Capybara/api/v1/captcha/image/${captchaId}`;
            imageResponse = await fetch(url);
        } catch (error) {
            console.error('獲取驗證碼圖片失敗:', error);
            return { captchaId: '', captchaText: '' };
        }

        const imageBlob = await imageResponse.blob();
        const base64String = await blobToBase64(imageBlob);
        const captchaText = await recognizeCaptcha(base64String);

        return {
            captchaId,
            captchaText
        };
    } catch (error) {
        console.error('取得驗證碼失敗:', error);
        return { captchaId: '', captchaText: '' };
    }
}

// 兌換禮包
export async function exchangeGift(gameId: string, giftCode: string): Promise<number> {
    try {
        let isServerBusy = true;
        while (isServerBusy) {
            isServerBusy = false;

            let { captchaId, captchaText } = await getCaptcha();
            if (!captchaId || !captchaText || captchaId === '' || captchaText === '') {
                ({ captchaId, captchaText } = await getCaptcha());
                if (!captchaId || !captchaText || captchaId === '' || captchaText === '') {
                    console.error('無法獲取有效的驗證碼');
                    return -1;
                }
            }

            let response;
            try {
                const url = 'https://prod-mail.habbyservice.com/Capybara/api/v1/giftcode/claim';
                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        userId: gameId,
                        giftCode: giftCode,
                        captcha: captchaText,
                        captchaId: captchaId
                    })
                });
            } catch (error) {
                console.error('兌換請求失敗:', error);
                return -1;
            }

            let result;
            try {
                result = await response.json();
            } catch (error) {
                console.error('解析回應失敗:', error);
                return -1;
            }

            if (![0, 20001, 20002, 20003, 20407, 20401, 20403, 30001].includes(result.code)) {
                console.log('未知的code', result);
                alert('未知的code: ' + result.code + ' ' + result.message);
            }

            if (result.code === 30001 || result.code === 20001) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                isServerBusy = true;
                continue;
            }
            return result.code;
        }

        return -1;

    } catch (error) {
        console.error('兌換失敗:', error);
        return -1;
    }
}
